// A reference by query in the card (decision 043, R-C4): the section, the saved filter and what it finds now; edited with the core's
// filter builder. The number is the server's — what the published list gives, past the rights of sections; the chips are the records
// of it the viewer's store holds.

import { useEffect, useMemo, useRef, useState } from 'react';
import { canRead, compile, queryLabel, queryValue, recordTitle, sortRecords, type FieldDef, type Filter, type Id, type QueryValue, type RecordObj } from '@confdeck/core-shared';
import { FilterBuilder, fromFlat, toFlat } from '../table/FilterBuilder.tsx';
import { scopeField, withScope } from '../shell/scope.tsx';
import { navigate } from '../shell/router.ts';
import { setField } from '../sync/mutations.ts';
import { useAccess, useMeta, useSync, useTypesTick } from '../sync/react.tsx';
import { Button, RecordChip } from '../ui/primitives.tsx';
import { Checkbox } from '../ui/controls.tsx';
import { Select } from '../ui/menus.tsx';
import { FitRow } from './FitRow.tsx';
import s from './fields.module.css';

/** The scope record of a holder (itself, or the one its own link to the scope type names) — what a scoped query keeps. */
function useHolderScope(rec: RecordObj): Id | null {
  const client = useSync();
  const meta = useMeta();
  const scopeTypeId = client.header?.scope?.typeId;
  if (!scopeTypeId) return null;
  if (rec.typeId === scopeTypeId) return rec.id;
  const f = scopeField(meta, rec.typeId, scopeTypeId);
  return f ? client.store.linked(rec, f)[0] ?? null : null;
}

/** The records a query finds in the browser's store: the section, the holder's scope when asked, the filter. */
function useEntries(rec: RecordObj, q: QueryValue | null): RecordObj[] {
  const client = useSync();
  const meta = useMeta();
  const scopeId = useHolderScope(rec);
  const tick = useTypesTick(q ? [q.type] : []);
  return useMemo(() => {
    if (!q || !meta.type(q.type)) return [];
    const scopeTypeId = client.header?.scope?.typeId;
    const needsScope = q.scoped || JSON.stringify(q.filter ?? null).includes('"$scope"');
    if (needsScope && !scopeId) return [];
    let filter: Filter | null = q.filter;
    if (q.scoped && q.type === scopeTypeId) filter = filter ? { and: [filter, { field: 'id', op: 'in', value: [scopeId] }] } : { field: 'id', op: 'in', value: [scopeId] };
    else if (q.scoped) {
      const f = scopeField(meta, q.type, scopeTypeId);
      if (!f) return [];
      filter = withScope(filter, f, scopeId);
    } else filter = withScope(filter, undefined, scopeId);
    const pass = filter ? compile(client.store, q.type, filter) : () => true;
    const list = [...client.store.recordIds(q.type)].map((id) => client.store.record(id)).filter((r): r is RecordObj => !!r && pass(r));
    return sortRecords(client.store, q.type, [{ field: meta.type(q.type)!.titleField, dir: 'asc' }], list);
  }, [q, scopeId, tick, meta, client]);
}

/**
 * How many records the published list gives for this query now (decision 043 п.1), saved or a draft: the server counts by the same rule
 * as the site. null — not known yet; 'closed' — the section is closed to the viewer, so only what they see is counted.
 */
function usePublishedCount(rec: RecordObj, q: QueryValue | null): number | 'closed' | null {
  const scopeId = useHolderScope(rec);
  const tick = useTypesTick(q ? [q.type] : []);
  const [n, setN] = useState<number | 'closed' | null>(null);
  const value = q ? JSON.stringify(q) : '';
  useEffect(() => {
    if (!value) { setN(null); return; }
    let live = true;
    // A newer edit drops the request still on its way: the server does not count a stale query.
    const stop = new AbortController();
    // A pause: a draft changes with every keystroke of a condition.
    const t = setTimeout(() => {
      void fetch(`/api/query/count?holder=${rec.id}&value=${encodeURIComponent(value)}`, { signal: stop.signal })
        .then(async (r) => { if (live) setN(r.ok ? ((await r.json()) as { count: number }).count : r.status === 403 ? 'closed' : null); })
        .catch(() => { if (live) setN(null); });
    }, 250);
    return () => { live = false; clearTimeout(t); stop.abort(); };
  }, [rec.id, value, scopeId, tick]);
  return n;
}

/** The number in words: the server's, or — for a section closed to the viewer — what they see, said so. */
const countText = (n: number | 'closed' | null, seen: number) => n === 'closed' ? `видно вам: ${seen}` : n === null ? '…' : String(n);

/** The value: «Записи · 2 условия», and in the card the number it gives and the records of it the viewer sees. */
export function QueryValueView({ rec, field, wrap }: { rec: RecordObj; field: FieldDef; wrap?: boolean }) {
  const meta = useMeta();
  const client = useSync();
  const q = queryValue(rec.data[field.key]);
  const entries = useEntries(rec, wrap ? q : null);
  const n = usePublishedCount(rec, wrap ? q : null);
  if (!q) return null;
  const label = queryLabel(meta, q, client.header?.scope?.label);
  if (!wrap) return <span className={s.clamp}>{label}</span>;
  const icon = meta.type(q.type)?.icon;
  return (
    <span className={s.query}>
      <span className={s.queryHead}>{label} · найдено: {countText(n, entries.length)}</span>
      {entries.length > 0 && <FitRow wrap items={entries.slice(0, 40).map((r) => ({ key: r.id, label: recordTitle(r, meta), node: <RecordChip icon={icon} onClick={(e) => { e.stopPropagation(); navigate(`/r/${r.id}`); }}>{recordTitle(r, meta)}</RecordChip> }))} total={entries.length} />}
    </span>
  );
}

/**
 * The editor: the section, the conditions of the core's filter builder and «the same scope record». The draft is written when the
 * popover closes or on «Готово»: every keystroke of a condition is not a command.
 */
export function QueryEditor({ rec, field, onDone }: { rec: RecordObj; field: FieldDef; onDone: () => void }) {
  const client = useSync();
  const meta = useMeta();
  const access = useAccess();
  const scope = client.header?.scope;
  const [draft, setDraft] = useState<QueryValue | null>(() => queryValue(rec.data[field.key]));
  const latest = useRef(draft);
  latest.current = draft;
  const saved = useRef(JSON.stringify(queryValue(rec.data[field.key])));
  const save = () => {
    const v = latest.current;
    const json = JSON.stringify(v);
    if (json === saved.current) return;
    saved.current = json;
    setField(client, rec, field.key, v);
  };
  // Closing the popover by a click outside saves too, as a long text does.
  useEffect(() => () => save(), []);
  const entries = useEntries(rec, draft);
  const n = usePublishedCount(rec, draft);
  const types = meta.types().filter((t) => !t.archivedAt && canRead(access, t.id)).sort((a, b) => a.nameMany.localeCompare(b.nameMany, 'ru'));
  const scopable = !!draft && !!scope && (draft.type === scope.typeId || !!scopeField(meta, draft.type, scope.typeId));
  return (
    <div className={s.queryBox}>
      <Select label="Раздел" value={draft?.type ?? ''} placeholder="Выберите раздел…" search
        options={types.map((t) => ({ value: t.id, label: t.nameMany }))}
        onChange={(v) => setDraft({ type: v, filter: null, ...(scope && (v === scope.typeId || scopeField(meta, v, scope.typeId)) ? { scoped: true } : {}) })} />
      {draft && (
        <>
          <FilterBuilder typeId={draft.type} value={toFlat(draft.filter)} onChange={(ff) => setDraft({ ...draft, filter: fromFlat(ff) })} />
          {scopable && (
            <Checkbox checked={!!draft.scoped} onChange={(v) => setDraft({ ...draft, scoped: v || undefined })}>
              Только той же записи «{scope!.label}», что и эта
            </Checkbox>
          )}
          <div className={s.queryFoot}>
            <span className={s.clamp}>Сейчас подходит: {countText(n, entries.length)}</span>
            <Button size="sm" variant="primary" onClick={() => { save(); onDone(); }}>Готово</Button>
          </div>
        </>
      )}
    </div>
  );
}
